/**
 * WHAT SCOUT IS DOING, SAID ONLY WHEN THE RECORD SAYS SO.
 *
 * THE OWNER ASKED FOR MOTION WHILE SCOUT IS THINKING, RESEARCHING OR
 * EXPERIMENTING, AND THE WHOLE DIFFICULTY IS THE WORD "IS". An interface that
 * animates on a timer is making a claim about the present tense, and this
 * product's record almost never stores the present tense — it stores what
 * happened, when it happened, and whether anything has happened since. So the
 * rule this module is built on is narrow and absolute:
 *
 *   MOTION IS EMITTED BY A FUNCTION OF A RECORD, NEVER BY A CLOCK.
 *
 * Every reading below is derived from a field that a Worker wrote. Where no
 * field supports a claim, the reading says so in its own words — and where a
 * state in the vocabulary has no producer at all, it is DECLARED AND MARKED
 * UNREACHABLE rather than quietly rendered by something that guessed.
 *
 * ---------------------------------------------------------------------------
 * THE ELEVEN NAMES, AND WHERE EACH ONE ACTUALLY COMES FROM
 * ---------------------------------------------------------------------------
 *
 *   IDLE                nothing outstanding, and the record says nothing is
 *                       running. The absence of work, not an unknown.
 *   REQUEST_PENDING     a chat request this browser sent that has not been
 *                       answered, or a turn the SERVER reports as `IN_FLIGHT`.
 *                       A pending HTTP request can truthfully animate as
 *                       "waiting for Scout"; it is NOT evidence that Scout is
 *                       reasoning, and the wording says exactly that.
 *   RESEARCH_RUNNING    `scout_research_runs.state = RUNNING`, before the
 *                       deadline, with `last_segment_at` inside the driver's
 *                       own cadence margin. The only state in this vocabulary
 *                       backed by a WRITE that a running lane produced.
 *   EVIDENCE_REVIEW     *** UNREACHABLE IN THIS BUILD — SEE BELOW ***
 *   EXPERIMENT_RUNNING  an experiment at status `RUNNING` WITH a lab agent
 *                       present to run it. Without the agent the same status
 *                       means the opposite, and reads as UNKNOWN.
 *   PAUSED              a research SESSION the Owner paused
 *                       (`PAUSED_BY_OWNER`). Not a window — windows have no
 *                       paused state, and inventing one is forbidden.
 *   CANCELLING          `state = RUNNING` AND `cancel_requested = true`. The
 *                       Owner's stop is recorded and no further segment will
 *                       start, which is why NOTHING MOVES here.
 *   STOPPED             `state = STOPPED` / `endReason = OWNER_CANCELLED` or
 *                       `BUDGET_EXHAUSTED`. Terminal; the window ended because
 *                       the Owner stopped it or the spend envelope was spent.
 *   COMPLETED           `state = COMPLETED` (`WINDOW_ENDED` or Scout's own
 *                       conclusion). Terminal, and the ordinary end.
 *   DEADLINE_REACHED    `state = COMPLETED` (the deadline IS how a window ends
 *                       on time), OR the clock has passed `deadline_at` with
 *                       no write yet — which is a DIFFERENT fact and says so.
 *   FAILED              `state = FAILED` (`MACHINERY_UNRECOVERABLE`,
 *                       `PROVIDER_UNAVAILABLE` or `RECOVERY_EXHAUSTED`), or a
 *                       chat attempt the server recorded as failed.
 *   UNKNOWN             the record cannot say. This is a real answer and it is
 *                       never dressed up as one of the others.
 *
 * WHY `EVIDENCE_REVIEW` IS UNREACHABLE, AND WHY IT IS STILL WRITTEN DOWN.
 *
 * The brief lists it among the state names, and the Owner does want to see when
 * Scout is working on evidence. Nothing in this build can say it. A segment does
 * a search, then reads pages, then adjudicates — and the run row records only
 * THAT a segment ran and WHEN it last did (`segments_run`, `last_segment_at`).
 * It does not record which phase a segment is in. So a screen that showed
 * "reviewing evidence" would be inferring a phase from a timestamp, which is
 * precisely the fabricated thinking step the brief forbids.
 *
 * The alternative — inventing a `phase` column so the animation has something
 * true to read — is a change to the research engine, and the brief is explicit
 * that a visual polish task does not get to make one.
 *
 * So it is declared here, marked unreachable with the reason, and pinned by a
 * specimen. A later slice that records the phase deletes a line that says why
 * it must not be guessed at, which is the same device
 * `SCOUT_SEARCH_NEVER_SEARCHED_COLUMNS` uses for a column that must not be
 * widened into.
 */

/**
 * WHEN THIS WORKSPACE CALLS A WINDOW'S LANE ALIVE.
 *
 * NOT A BACKEND FACT, AND IT DOES NOT PRETEND TO BE. The backend records when a
 * segment last ran; it does not record "is this healthy", because that is a
 * judgement about a cadence and the cadence belongs to the driver. The driver's
 * floor between segments is sixty seconds, so five minutes of silence is this
 * workspace's own margin — stated here so a reader can disagree with it, which
 * they could not do with an unexplained spinner.
 *
 * IT LIVES HERE RATHER THAN IN ANY ONE VIEW because two screens now ask it. A
 * Research Control screen that called a window live while the Main Chat's
 * activity mark called the same window quiet would be two answers to one
 * question, and the Owner would have no way to tell which was lying.
 */
export const QUIET_AFTER_MS = 5 * 60 * 1_000;

/** The fields a window's state is derived from, named structurally so this
 *  module can be proved without importing a view. */
export interface WindowRecord {
	state: string;
	cancelRequested: boolean;
	deadlineAt: string;
	lastSegmentAt: string | null;
	endReason?: string | null;
}

/** WHAT THIS WORKSPACE CONCLUDES ABOUT A WINDOW, WHICH IS NOT THE SAME AS WHAT
 *  IS STORED. Kept apart from `ActivityState` on purpose: a stored fact and a
 *  rendered claim are two different things and a reader should be able to see
 *  where the line is.
 *
 *  THERE IS DELIBERATELY NO `RUNNING_QUIET` HERE, AND IT WAS TRIED. Slice 10's
 *  first draft added one, and it broke two things at once: the Research Control
 *  screen renders this name as a tag, and its own vocabulary table lists the
 *  names it says a reader might expect — so a new name invented here would have
 *  appeared on the screen without appearing in the table that explains it. The
 *  cadence judgement belongs to `runActivity`, which is where a claim about the
 *  present tense is made; this function answers the narrower question of what
 *  the record says. */
export type WindowDisplayState =
	| 'RUNNING'
	| 'CANCELLING'
	| 'DEADLINE_PASSED_UNOBSERVED'
	| 'STOPPED'
	| 'COMPLETED'
	| 'FAILED'
	| 'UNRECOGNISED';

export function displayStateOf(run: WindowRecord, observedAtMs: number): WindowDisplayState {
	// THE TERMINAL STATES ARE READ, NEVER INFERRED. A window that ended says so
	// in its own row, with the reason it ended for.
	if (run.state === 'STOPPED') return 'STOPPED';
	if (run.state === 'COMPLETED') return 'COMPLETED';
	if (run.state === 'FAILED') return 'FAILED';
	if (run.state !== 'RUNNING') return 'UNRECOGNISED';
	// A CANCEL IS A DURABLE FACT AND IS SHOWN AS ITS OWN THING, because "you
	// asked it to stop and the tick has not come round yet" is a different state
	// from "it is running" — and a screen that showed the second would make the
	// Owner press the button again.
	if (run.cancelRequested) return 'CANCELLING';
	// THE CLOCK RAN OUT AND NOTHING HAS OBSERVED IT YET. This is NOT COMPLETED —
	// completion is a write, and no write has happened — and it is emphatically
	// not RUNNING, which is what a timer-driven screen would show.
	if (Date.parse(run.deadlineAt) <= observedAtMs) return 'DEADLINE_PASSED_UNOBSERVED';
	return 'RUNNING';
}

/**
 * WHETHER A RUNNING WINDOW'S LANE HAS GONE QUIET — the workspace's own
 * judgement, stated once so the two screens that ask it cannot disagree.
 *
 * A WINDOW WITH NO SEGMENT YET IS NOT QUIET. It opened seconds ago and its
 * first segment is due on the next tick; calling that quiet would report the
 * driver's ordinary start-up as a fault.
 *
 * A TIMESTAMP THIS BUILD CANNOT READ IS QUIET, WHICH IS THE CONSERVATIVE
 * DIRECTION. `NaN` comparisons are false, so an unparseable `last_segment_at`
 * that fell through to "not quiet" would animate a lane nobody has heard from.
 */
export function laneIsQuiet(run: WindowRecord, observedAtMs: number): boolean {
	if (run.lastSegmentAt === null) return false;
	const since = observedAtMs - Date.parse(run.lastSegmentAt);
	return !Number.isFinite(since) || since > QUIET_AFTER_MS;
}

export const DISPLAY_CAPTIONS: Record<WindowDisplayState, string> = {
	RUNNING: 'the window is open, its deadline is ahead, and no stop has been recorded',
	CANCELLING: 'your stop is recorded; the window ends at its next tick and starts no further segment',
	DEADLINE_PASSED_UNOBSERVED:
		'the authorized window ended and nothing has observed it yet — this is NOT complete until the record says so',
	STOPPED: 'ended because you stopped it',
	COMPLETED: 'ended because the authorized window ran out — the ordinary end',
	FAILED: 'ended because no segment could start, three times running',
	UNRECOGNISED: 'this build does not recognise the state on this row',
};

/* ------------------------------------------------------------- the vocabulary */

export type ActivityState =
	| 'IDLE'
	| 'REQUEST_PENDING'
	| 'RESEARCH_RUNNING'
	| 'EVIDENCE_REVIEW'
	| 'EXPERIMENT_RUNNING'
	| 'PAUSED'
	| 'CANCELLING'
	| 'STOPPED'
	| 'COMPLETED'
	| 'DEADLINE_REACHED'
	| 'FAILED'
	| 'UNKNOWN';

/**
 * WHAT EACH NAME IS ALLOWED TO CLAIM, AND WHAT PRODUCES IT.
 *
 * EVERY ENTRY CARRIES A SENTENCE, INCLUDING THE UNPRODUCED ONE, AND THAT IS THIS
 * REPOSITORY'S OWN CONVENTION RATHER THAN AN INVENTION. `SCOUT_NOTIFICATION_PRODUCERS`
 * does the same for the three notification classes nothing raises — the sentence
 * begins with `NOTHING` and a consumer marks the row on that prefix (see
 * `views/notifications.tsx`). The alternative, a `null`, would be a second
 * spelling of one idea and would say less: a reader who finds an omitted state
 * needs to know WHY it cannot be produced, not merely that it cannot.
 */
export const ACTIVITY_VOCABULARY: ReadonlyArray<{
	name: ActivityState;
	/** WHO RAISES IT, or a sentence beginning with `NOTHING` saying why nobody
	 *  can. `views/activity-mark.tsx` marks those rows on that prefix. */
	producer: string;
}> = [
	{ name: 'IDLE', producer: 'the absence of any outstanding request, open window or running experiment' },
	{ name: 'REQUEST_PENDING', producer: '`scout_messages.answer_state = PENDING` with an `IN_FLIGHT` outlook, or a send this browser has not seen answered' },
	{ name: 'RESEARCH_RUNNING', producer: '`scout_research_runs.state = RUNNING` with `last_segment_at` inside the cadence margin' },
	{
		name: 'EVIDENCE_REVIEW',
		producer:
			'NOTHING raises this. No column records which phase of a segment is in progress — `last_segment_at` says WHEN a segment last ran and never WHAT it is doing — and inferring a phase from that timestamp is exactly the fabricated thinking step this product forbids. A later slice that records the phase deletes this sentence.',
	},
	{ name: 'EXPERIMENT_RUNNING', producer: 'an experiment at status `RUNNING` with a lab agent present to run it' },
	{ name: 'PAUSED', producer: 'a research session the Owner paused (`PAUSED_BY_OWNER`); windows have no paused state' },
	{ name: 'CANCELLING', producer: '`state = RUNNING` AND `cancel_requested = true`' },
	{ name: 'STOPPED', producer: '`state = STOPPED` with `endReason = OWNER_CANCELLED` or `BUDGET_EXHAUSTED`' },
	{ name: 'COMPLETED', producer: '`state = COMPLETED` with `endReason = WINDOW_ENDED` or Scout’s own conclusion' },
	{ name: 'DEADLINE_REACHED', producer: 'the same terminal record as COMPLETED, or `deadline_at` passed with no end write yet — the two are said differently' },
	{ name: 'FAILED', producer: '`state = FAILED` (`MACHINERY_UNRECOVERABLE`, `PROVIDER_UNAVAILABLE` or `RECOVERY_EXHAUSTED`), or a chat attempt the server recorded as failed' },
	{ name: 'UNKNOWN', producer: 'the record cannot say, or this build does not recognise what it says' },
];

/** One reading: a state, the sentence that justifies it, and whether anything
 *  is permitted to move because of it. */
export interface ActivityReading {
	state: ActivityState;
	/** TRUE ONLY WHEN THE RECORD SUPPORTS "AND IT IS STILL HAPPENING". Every
	 *  caller that animates must read this rather than the state name, so a
	 *  terminal state cannot end up with a spinner by accident. */
	moving: boolean;
	/** What the mark says to a reader. Always about the RECORD, never about
	 *  Scout's inner life. */
	wording: string;
}

const IDLE: ActivityReading = {
	state: 'IDLE',
	moving: false,
	wording: 'Nothing is outstanding. Scout is not working on anything this workspace can see.',
};

/* --------------------------------------------------------------- the readings */

/**
 * A CHAT REQUEST — the one place a pending request may animate, and the exact
 * limit of what it may say.
 *
 * `sending` IS THIS BROWSER'S OWN STATE AND THE WORDING ADMITS IT. A request
 * that has left and not been answered proves one thing: no answer has arrived.
 * It does NOT prove Scout is reasoning — the Worker may be queued, the network
 * may be slow, or the answer may already exist and the response be lost. So the
 * mark says "waiting for Scout" and names the elapsed time, which is a fact,
 * rather than "Scout is thinking", which is not.
 *
 * `inFlight` IS THE SERVER'S OWN WORD AND IS TRUSTED FURTHER. It is written by
 * the turn-attempt record and means an attempt is claimed and cannot be
 * accounted for — see `core/answer-attempt.ts`. It still does not say what
 * Scout is doing inside that attempt, so the wording stays "an attempt is
 * running", never "Scout is reasoning".
 */
export function chatActivity(input: {
	/** The view's own pending turn, or `none`. */
	pending: 'none' | 'sending' | 'failed' | 'inFlight' | 'uncertain';
	/** The newest recorded turn's outlook, when the record carried one. */
	newestOutlook?: 'ANSWERED' | 'FAILED' | 'IN_FLIGHT' | 'UNCERTAIN' | 'UNRECORDED';
	elapsedMs?: number;
}): ActivityReading {
	if (input.pending === 'failed') {
		return { state: 'FAILED', moving: false, wording: 'The last request did not complete. The record says so, and retrying is safe.' };
	}
	if (input.pending === 'uncertain') {
		return {
			state: 'UNKNOWN',
			moving: false,
			wording:
				'An attempt was claimed and cannot be accounted for. This is NOT a failure and NOT progress — only you can decide what to do about it.',
		};
	}
	if (input.pending === 'sending') {
		return {
			state: 'REQUEST_PENDING',
			moving: true,
			wording:
				'Waiting for Scout to answer a request this browser sent. That the request is outstanding is certain; what Scout is doing with it is not recorded here.',
		};
	}
	if (input.pending === 'inFlight') {
		return {
			state: 'REQUEST_PENDING',
			moving: true,
			wording:
				'The record says an attempt on this turn is still running and has not been accounted for. How long it has been running is a fact; what it is doing is not recorded.',
		};
	}
	if (input.newestOutlook === 'IN_FLIGHT') {
		return {
			state: 'REQUEST_PENDING',
			moving: true,
			wording: 'The newest recorded turn has an attempt the server reports as in flight.',
		};
	}
	if (input.newestOutlook === 'UNCERTAIN') {
		return {
			state: 'UNKNOWN',
			moving: false,
			wording: 'The newest recorded turn has an attempt that cannot be accounted for.',
		};
	}
	if (input.newestOutlook === 'FAILED') {
		return { state: 'FAILED', moving: false, wording: 'The newest recorded turn recorded a failure.' };
	}
	return IDLE;
}

/**
 * A RESEARCH WINDOW.
 *
 * `RUNNING_QUIET` MAPS TO `UNKNOWN` AND THAT IS THE WHOLE POINT OF THIS
 * FUNCTION. A window whose deadline is ahead and whose last segment is twelve
 * minutes old is a window whose record cannot distinguish "working on something
 * long" from "the lane is stuck". Showing either would be a guess. So the mark
 * stops moving and says which two things it cannot tell apart.
 */
export function runActivity(run: WindowRecord, observedAtMs: number): ActivityReading {
	const display = displayStateOf(run, observedAtMs);
	switch (display) {
		case 'RUNNING':
			// THE CADENCE JUDGEMENT IS MADE HERE AND NOT IN `displayStateOf`,
			// because the record's own state is RUNNING and always will be — a
			// window between segments is not a different state, it is a window
			// nobody has heard from. What changes is what may be CLAIMED.
			if (laneIsQuiet(run, observedAtMs)) {
				return {
					state: 'UNKNOWN',
					moving: false,
					wording:
						'The window is still open, but no segment has run inside the driver’s cadence. This record cannot tell a long segment from a stopped lane, so nothing here claims either.',
				};
			}
			return {
				state: 'RESEARCH_RUNNING',
				moving: true,
				wording:
					run.lastSegmentAt === null
						? 'The window is open and no segment has run yet — the first is due on the next tick.'
						: `The window is open and a segment ran within the last ${Math.round(QUIET_AFTER_MS / 60_000)} minutes.`,
			};
		case 'CANCELLING':
			return {
				state: 'CANCELLING',
				moving: false,
				wording: 'Your stop is recorded. The window ends at its next tick and starts no further segment.',
			};
		case 'DEADLINE_PASSED_UNOBSERVED':
			return {
				state: 'DEADLINE_REACHED',
				moving: false,
				wording:
					'The authorized window has ended by its own clock and no end has been written yet. This is not complete until the record says so.',
			};
		case 'STOPPED':
			return { state: 'STOPPED', moving: false, wording: 'The window ended because you stopped it.' };
		case 'COMPLETED':
			return { state: 'COMPLETED', moving: false, wording: 'The window ended because the authorized time ran out.' };
		case 'FAILED':
			return { state: 'FAILED', moving: false, wording: 'The window ended because no segment could start, three times running.' };
		default:
			return {
				state: 'UNKNOWN',
				moving: false,
				wording: 'This build does not recognise the state on this window’s record.',
			};
	}
}

/**
 * AN EXPERIMENT.
 *
 * THE LAB AGENT IS PART OF THE READING AND NOT A FOOTNOTE. `status = RUNNING`
 * is written by the run stage; whether anything is executing it is a separate
 * fact held by `GET /gateway/lab/status`. An experiment recorded RUNNING with no
 * agent polling for work is not an experiment in progress — it is a row waiting
 * for a process that is not there, and the two must not look alike.
 */
export function experimentActivity(input: {
	status: string;
	/** `null` when the lab status could not be read — which is NOT the same as
	 *  "no agent", and is not treated as one. */
	labAgentPresent: boolean | null;
}): ActivityReading {
	if (input.status === 'RUNNING') {
		if (input.labAgentPresent === true) {
			return { state: 'EXPERIMENT_RUNNING', moving: true, wording: 'The experiment is recorded as running and a lab agent is present to run it.' };
		}
		if (input.labAgentPresent === false) {
			return {
				state: 'UNKNOWN',
				moving: false,
				wording:
					'The experiment is recorded as running but no lab agent has polled for work, so nothing is executing it.',
			};
		}
		return {
			state: 'UNKNOWN',
			moving: false,
			wording:
				'The experiment is recorded as running and whether a lab agent is present could not be read, so this cannot say it is executing.',
		};
	}
	if (input.status === 'FAILED') {
		return { state: 'FAILED', moving: false, wording: 'The experiment is recorded as failed.' };
	}
	if (input.status === 'COMPLETED') {
		return { state: 'COMPLETED', moving: false, wording: 'The experiment is recorded as complete.' };
	}
	if (input.status === 'QUEUED' || input.status === 'PROPOSED') {
		return {
			state: 'IDLE',
			moving: false,
			wording: `The experiment is at status ${input.status}. Nothing is running.`,
		};
	}
	return { state: 'UNKNOWN', moving: false, wording: `This build does not recognise the experiment status “${input.status}”.` };
}

/**
 * THE ONE READING A SCREEN SHOWS WHEN SEVERAL THINGS COULD BE TRUE.
 *
 * FIRST BY SEVERITY, THEN BY NAME — and the order is a product decision rather
 * than an aesthetic one. Something that is FAILED or UNKNOWN outranks something
 * that is moving, because a mark that showed a cheerful orbit while a window had
 * failed would be the surface hiding the fact the Owner most needs. The tie
 * break is the vocabulary's own order, so two screens given the same set of
 * readings cannot disagree.
 *
 * A NAME THE TABLE DOES NOT CARRY RANKS LAST RATHER THAN FIRST — see the note in
 * `mostSevere`, where the first version of this got that backwards.
 */
const SEVERITY: readonly ActivityState[] = [
	'FAILED',
	'UNKNOWN',
	'DEADLINE_REACHED',
	'CANCELLING',
	'RESEARCH_RUNNING',
	'EXPERIMENT_RUNNING',
	'REQUEST_PENDING',
	'PAUSED',
	'STOPPED',
	'COMPLETED',
	'EVIDENCE_REVIEW',
	'IDLE',
];

export function mostSevere(readings: readonly ActivityReading[]): ActivityReading {
	let best: ActivityReading | null = null;
	let bestRank = Number.POSITIVE_INFINITY;
	for (const reading of readings) {
		const found = SEVERITY.indexOf(reading.state);
		// AN UNKNOWN STATE RANKS LAST, AND THE FIRST VERSION OF THIS GOT IT
		// EXACTLY BACKWARDS. `indexOf` answers `-1` for a name the table does
		// not carry, and `-1` is the SMALLEST rank — so a state added to the
		// union and forgotten here would have OUTRANKED `FAILED` and won, which
		// is the opposite of what this function is for and the opposite of what
		// the comment above `SEVERITY` claimed. It was unreachable (the table
		// carries all twelve names and nothing in the workspace called this
		// function), so no specimen could have caught it; it was found by
		// reading the code against its own comment. Mapping the miss to one past
		// the end is what makes the comment true.
		const rank = found === -1 ? SEVERITY.length : found;
		if (rank < bestRank) {
			best = reading;
			bestRank = rank;
		}
	}
	return best ?? IDLE;
}
